import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider, useToast } from './context/ToastContext';
import { Button, Portal } from './components/ui';
import Login from './screens/Login';
import Navigation from './Navigation';
import SplashScreen from './screens/SplashScreen';
import StaffOnboarding from './screens/StaffOnboarding';
import ProDetails from './screens/ProDetails';
import CustomerOnboarding from './screens/CustomerOnboarding';
import StaffSetup from './screens/StaffSetup';
import AccessEnded from './screens/AccessEnded';
import Landing from './site/Landing';
import Privacy from './site/Privacy';
import DeleteAccount from './site/DeleteAccount';
import GalleryPage from './site/GalleryPage';

/**
 * The signed-in area (/app/*).
 *
 * This is a 1:1 port of the mobile app's App.js Root() gate, so a user sees the
 * exact same screen at the same point in their journey on web as in the app.
 */
function AppArea() {
  const { isAuthed, user, restoring } = useAuth();
  const location = useLocation();

  if (restoring) return null; // brief; the splash covers the first paint

  // Not signed in → send them to login, remembering where they were headed.
  if (!isAuthed) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  if (user?.role === 'staff') {
    // Not yet verified → collect personal details first, then verification.
    if (user.kyc?.status !== 'approved') {
      if (!user.proProfile?.completed) return <ProDetails />;
      return <StaffOnboarding />;
    }
    // Offboarded (past leaving date) → access ended.
    if (user.hasLeft) return <AccessEnded />;
    // Approved → pick service locations & categories once.
    if (!(user.serviceAreas && user.serviceAreas.length)) return <StaffSetup />;
  }

  // New customers set their name (+ optional address) before entering.
  if (user?.role === 'customer' && !(user?.name && user.name.trim())) return <CustomerOnboarding />;

  return <Navigation />;
}

/* The login screen, with the splash in front of it on first visit. Signing in
   flips `isAuthed`, which bounces straight through to the app. */
function LoginRoute() {
  const { isAuthed, restoring } = useAuth();
  const location = useLocation();
  const [showSplash, setShowSplash] = useState(true);

  if (restoring) return null;
  // An already-signed-in visitor shouldn't sit through the splash.
  if (isAuthed) return <Navigate to={location.state?.from || '/app'} replace />;
  if (showSplash) return <SplashScreen onDone={() => setShowSplash(false)} />;
  return <Login />;
}

/* Toasts + confirm dialog — the web stand-in for React Native's Alert. */
function Overlays() {
  const { toasts, dialog, closeDialog } = useToast();
  return (
    <Portal>
      <div className="toast-host" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone !== 'default' ? `toast--${t.tone}` : ''}`}>
            {t.title ? <div className="toast__title">{t.title}</div> : null}
            {t.message}
          </div>
        ))}
      </div>

      {dialog && (
        <div className="modal-backdrop modal-backdrop--center" style={{ zIndex: 250 }} onClick={() => closeDialog(false)}>
          <div className="card" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 400, width: '100%', borderRadius: 'var(--r-xl)' }} role="alertdialog" aria-modal="true">
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>{dialog.title}</h3>
            {dialog.message ? (
              <p className="muted" style={{ marginTop: 8, lineHeight: 1.5 }}>{dialog.message}</p>
            ) : null}
            <div className="btn-row" style={{ marginTop: 20 }}>
              <Button variant="ghost" title={dialog.cancelLabel} onClick={() => closeDialog(false)} />
              <Button variant={dialog.danger ? 'danger' : 'primary'} title={dialog.confirmLabel} onClick={() => closeDialog(true)} />
            </div>
          </div>
        </div>
      )}
    </Portal>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            {/* Public website */}
            <Route path="/" element={<Landing />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/delete-account" element={<DeleteAccount />} />

            {/* Sign in */}
            <Route path="/login" element={<LoginRoute />} />

            {/* The app itself */}
            <Route path="/app/*" element={<AppArea />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Overlays />
      </ToastProvider>
    </AuthProvider>
  );
}
